/**
 * The folders behind the Home scene and the new-document dialog — through the
 * shared wire client the web app uses too (`@shared/client/folders`), so what
 * Word sends and what the web sends is byte-identical.
 *
 * Plain state + effects, the add-in's convention (it has no query cache). The
 * hook re-fetches when its workspace scope changes and exposes a `reload` so a
 * create/rename/delete can refresh the list it belongs to.
 *
 * Folders only: submissions (the eCTD leaf sets) are a web-surface concept the
 * add-in does not render yet. The server still understands them, so folder data
 * the web app writes is never lost — see the folders-and-submissions design.
 */

import {
  type Folder,
  createFolder,
  deleteFolder,
  listFolders,
  moveDocumentToFolder,
  renameFolder,
} from "@shared/client/folders";
import { useCallback, useEffect, useMemo, useState } from "react";
import { sharedClientConfig } from "../../api/client";

export interface FoldersState {
  /** `undefined` while loading. */
  folders: Folder[] | undefined;
  error: string | null;
  reload: () => void;
  create: (label: string) => Promise<Folder>;
  /** One record; no member is touched. */
  rename: (folderId: string, label: string) => Promise<void>;
  /** Deletes the folder and UNFILES its members; deletes no document. */
  remove: (folderId: string) => Promise<void>;
  /** File a document into a folder, or unfile with null. Strictly
   *  per-document — moving one card never moves another. */
  moveDocument: (docId: string, folderId: string | null) => Promise<void>;
}

export function useFolders(workspaceIds: readonly string[]): FoldersState {
  const [folders, setFolders] = useState<Folder[] | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  // Joined on a separator no id contains, so several workspaces (Word's
  // "All workspaces" scope) stay distinct and the effect keys on one string.
  const key = workspaceIds.filter((id) => id.length > 0).join("|");
  const ids = useMemo(() => (key.length === 0 ? [] : key.split("|")), [key]);

  useEffect(() => {
    void nonce; // `reload` bumps it so the fetch below re-runs
    if (ids.length === 0) {
      setFolders([]);
      return;
    }
    let gone = false;
    setFolders(undefined);
    setError(null);
    listFolders(sharedClientConfig(), ids)
      .then((got) => {
        if (!gone) setFolders(got);
      })
      .catch((err: unknown) => {
        if (!gone) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      gone = true;
    };
  }, [ids, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const create = useCallback(
    async (label: string) => {
      const made = await createFolder(sharedClientConfig(), { label, workspaceId: ids[0] ?? "" });
      reload();
      return made;
    },
    [ids, reload],
  );

  const rename = useCallback(
    async (folderId: string, label: string) => {
      await renameFolder(sharedClientConfig(), folderId, label);
      reload();
    },
    [reload],
  );
  const remove = useCallback(
    async (folderId: string) => {
      await deleteFolder(sharedClientConfig(), folderId);
      reload();
    },
    [reload],
  );
  const moveDocument = useCallback(async (docId: string, folderId: string | null) => {
    await moveDocumentToFolder(sharedClientConfig(), docId, folderId);
  }, []);

  return { folders, error, reload, create, rename, remove, moveDocument };
}
